import { makeAutoObservable } from 'mobx';
import { DataConnection } from 'peerjs';

import {
  isNetworkDataTransfer,
  NetworkDataTransfer,
  NetworkDataType,
} from './NetworkDataTransfers';
import { config } from '../config';
import { ignoredNetworkDataDebugMessageTypes } from '../constants';

export class Connection {
  dataListeners: {
    listener: (data: NetworkDataTransfer) => void;
    type: NetworkDataType | null;
  }[] = [];
  disconnectionListeners: {
    listener: () => void;
  }[] = [];
  raw: DataConnection;
  /** The UUID is the ID of where you connected */
  connectedUUID: string;

  /** The UUID of the connection locally */
  localUUID: string;
  dataLog: NetworkDataTransfer[] = [];

  /** userId is the id of where you are connecting to */
  constructor(public userId: string, connection: DataConnection, localUUID: string) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.localUUID = localUUID;
    this.raw = connection;
    this.connectedUUID = connection.peer;

    connection.on('close', () =>
      this.disconnectionListeners.forEach(({ listener }) => listener()),
    );
  }

  send(data: NetworkDataTransfer) {
    if (config.debugMode) {
      if (!ignoredNetworkDataDebugMessageTypes.includes(data.type)) {
        console.info(
          `[TPG] Sending P2P message to ${this.connectedUUID} from ${this.localUUID}`,
          data,
        );
      }
    }

    this.raw.send({ ...data, userId: this.localUUID });
  }

  onData(data: unknown) {
    if (!isNetworkDataTransfer(data)) return;
    this.dataListeners
      .filter(({ type }) => type === null || type === data.type)
      .forEach(({ listener }) => listener(data));
    this.dataLog.push(data);
  }

  addDataListener(listener: (data: NetworkDataTransfer) => void): () => void;
  addDataListener<T extends NetworkDataType>(
    listener: (data: NetworkDataTransfer & { type: T }) => void,
    type: T,
  ): () => void;
  addDataListener(
    listener: (data: NetworkDataTransfer) => void,
    type: NetworkDataType | null = null,
  ): () => void {
    const dataListener = { listener, type };

    this.dataListeners.push(dataListener);

    return () => {
      const index = this.dataListeners.indexOf(dataListener);
      if (index === -1) return;
      this.dataListeners.splice(index, 1);
    };
  }

  addDisconnectionListener(listener: () => void): () => void {
    const disconnectionListener = { listener };

    this.disconnectionListeners.push(disconnectionListener);

    return () => {
      const index = this.disconnectionListeners.indexOf(disconnectionListener);
      if (index === -1) return;
      this.disconnectionListeners.splice(index, 1);
    };
  }
}
